import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import KeyOffIcon from "@mui/icons-material/KeyOff";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockResetIcon from "@mui/icons-material/LockReset";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { getToken } from "../../utils";
import { ExpiredPage } from "../ExpiredPage";

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
};

const ValidChip = ({ valid }) =>
  valid ? (
    <Chip label="有效" color="success" size="small" />
  ) : (
    <Chip label="已过期" color="default" size="small" />
  );

const daysFromNow = (days) =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

const TestAccountPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [expired, setExpired] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createNote, setCreateNote] = useState("");
  const [createWithMembership, setCreateWithMembership] = useState(true);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Password reset state
  const [resetTarget, setResetTarget] = useState(null);
  const [resetCredentials, setResetCredentials] = useState(null);

  // Expanded management panel
  const [managedEmail, setManagedEmail] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const handleApiError = useCallback((err, fallback) => {
    if (err.response?.data?.error === "token has expired") {
      setExpired(true);
      return;
    }
    setError(err.response?.data?.error || fallback);
  }, []);

  const fetchAccounts = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/admin/test-accounts"),
        authHeaders()
      );
      setAccounts(response.data.test_accounts || []);
    } catch (err) {
      handleApiError(err, "获取测试账号列表失败");
    } finally {
      setListLoading(false);
    }
  }, [handleApiError]);

  const fetchOverview = useCallback(
    async (email) => {
      setOverviewLoading(true);
      try {
        const response = await axios.get(
          getFullApiUrl(
            `/api/v1/admin/test-account/${encodeURIComponent(email)}`
          ),
          authHeaders()
        );
        setOverview(response.data);
      } catch (err) {
        setOverview(null);
        handleApiError(err, "获取账号详情失败");
      } finally {
        setOverviewLoading(false);
      }
    },
    [handleApiError]
  );

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const refreshAll = async () => {
    await fetchAccounts();
    if (managedEmail) {
      await fetchOverview(managedEmail);
    }
  };

  const runAction = async (label, fn) => {
    setActionLoading(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      setMessage(`${label}成功`);
      await refreshAll();
    } catch (err) {
      handleApiError(err, `${label}失败`);
    } finally {
      setActionLoading(false);
    }
  };

  const createAccount = async () => {
    setActionLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/admin/test-accounts"),
        {
          email: createEmail.trim(),
          password: createPassword,
          note: createNote.trim(),
          membership_days: createWithMembership ? 30 : 0,
        },
        authHeaders()
      );
      setCreateOpen(false);
      setCreateEmail("");
      setCreatePassword("");
      setCreateNote("");
      if (response.data.password) {
        setCreatedCredentials({
          email: response.data.email,
          password: response.data.password,
        });
      } else {
        setMessage(`已把现有账号 ${response.data.email} 标记为测试账号`);
      }
      await fetchAccounts();
    } catch (err) {
      handleApiError(err, "创建测试账号失败");
    } finally {
      setActionLoading(false);
    }
  };

  const resetPassword = async () => {
    const target = resetTarget;
    setResetTarget(null);
    setActionLoading(true);
    setError(null);
    setMessage(null);
    setResetCredentials(null);
    try {
      const response = await axios.post(
        getFullApiUrl(
          `/api/v1/admin/test-accounts/${target.user_id}/reset-password`
        ),
        {},
        authHeaders()
      );
      setResetCredentials({
        email: response.data.email,
        password: response.data.password,
      });
    } catch (err) {
      handleApiError(err, "重置密码失败");
    } finally {
      setActionLoading(false);
    }
  };

  const toggleManage = async (email) => {
    if (managedEmail === email) {
      setManagedEmail(null);
      setOverview(null);
      return;
    }
    setManagedEmail(email);
    setOverview(null);
    await fetchOverview(email);
  };

  const invalidateSession = () =>
    runAction("session 失效", () =>
      axios.post(
        getFullApiUrl("/api/v1/admin/test-account/invalidate-session"),
        { email: overview.user.email },
        authHeaders()
      )
    );

  const createManual = (days) =>
    runAction("创建手动会员", () =>
      axios.post(
        getFullApiUrl("/api/v1/admin/manual-membership"),
        {
          user_id: overview.user.id,
          membership_end_at: daysFromNow(days),
          reason: "测试账号（test_account 管理页创建）",
        },
        authHeaders()
      )
    );

  const updateManual = (days, label) =>
    runAction(label, () =>
      axios.put(
        getFullApiUrl(
          `/api/v1/admin/manual-membership/${overview.manual_membership.id}`
        ),
        { membership_end_at: daysFromNow(days) },
        authHeaders()
      )
    );

  const updateStripe = (days, label, status) =>
    runAction(label, () =>
      axios.put(
        getFullApiUrl("/api/v1/admin/test-account/stripe-subscription"),
        {
          email: overview.user.email,
          current_period_end: daysFromNow(days),
          ...(status ? { status } : {}),
        },
        authHeaders()
      )
    );

  if (expired) {
    return <ExpiredPage />;
  }

  const manual = overview?.manual_membership;
  const stripeSubs = overview?.stripe_subscriptions || [];
  const sessions = overview?.podcast_sessions || [];

  const managementPanel = overview && (
    <Stack spacing={2} sx={{ my: 1 }}>
      <Card variant="outlined">
        <CardHeader
          title="会员状态"
          subheader="验证「续费不重登」：先设为已过期 → App 上确认变非会员 → 恢复 30 天（模拟续费到账）→ App 不重登应自动恢复"
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            手动会员
          </Typography>
          {manual ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <ValidChip valid={manual.valid} />
                <Typography variant="body2">
                  到期：{formatTime(manual.membership_end_at)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<EventBusyIcon />}
                  disabled={actionLoading}
                  onClick={() => updateManual(-1, "设为已过期")}
                >
                  设为已过期
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<EventAvailableIcon />}
                  disabled={actionLoading}
                  onClick={() => updateManual(30, "恢复 30 天")}
                >
                  恢复 30 天
                </Button>
              </Stack>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={actionLoading}
              onClick={() => createManual(30)}
              sx={{ mb: 2 }}
            >
              创建手动会员（30 天）
            </Button>
          )}

          {stripeSubs.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Stripe 订阅（改写数据库记录，不经过 Stripe）
              </Typography>
              {stripeSubs.map((sub) => (
                <Typography key={sub.id} variant="body2">
                  <code>{sub.stripe_subscription_id}</code> · {sub.status} ·
                  到期 {formatTime(sub.current_period_end)}{" "}
                  <ValidChip valid={sub.valid} />
                </Typography>
              ))}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<EventBusyIcon />}
                  disabled={actionLoading}
                  onClick={() => updateStripe(-1, "订阅设为已过期")}
                >
                  设为已过期
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<EventAvailableIcon />}
                  disabled={actionLoading}
                  onClick={() => updateStripe(30, "订阅恢复 30 天", "active")}
                >
                  恢复 30 天（active）
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title="Podcast Session"
          subheader="验证「session 失效自愈」：App 播放会员节目时点下方按钮 → App 应短暂重试后自动恢复完整音频，无需重登"
          sx={{ pb: 0 }}
        />
        <CardContent>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              无 session 记录（客户端首次鉴权时会自动补建）
            </Typography>
          ) : (
            sessions.map((s) => (
              <Typography key={s.podcast_id} variant="body2">
                {s.podcast_id} · <code>{s.token_preview}</code> · 更新于{" "}
                {formatTime(s.updated_at)}
              </Typography>
            ))
          )}
          <Button
            sx={{ mt: 1 }}
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<KeyOffIcon />}
            disabled={actionLoading || sessions.length === 0}
            onClick={invalidateSession}
          >
            使 session 失效（轮换 token）
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title="账号"
          subheader="重置后旧密码立即失效，新密码仅显示一次；已登录的设备不受影响"
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockResetIcon />}
            disabled={actionLoading}
            onClick={() =>
              setResetTarget({
                user_id: overview.user.id,
                email: overview.user.email,
              })
            }
          >
            重置密码
          </Button>
        </CardContent>
      </Card>
    </Stack>
  );

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h5">测试账号管理</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        创建专用测试账号并在手机 App
        上登录后，可在此模拟会员过期/续费与 session
        失效，验证客户端无需重登即可自动恢复。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}
      {createdCredentials && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setCreatedCredentials(null)}
        >
          <AlertTitle>
            测试账号已创建 —
            密码仅显示这一次，请立即保存（忘记可用「重置密码」重新生成）
          </AlertTitle>
          邮箱：<code>{createdCredentials.email}</code>
          <IconButton
            size="small"
            onClick={() =>
              navigator.clipboard.writeText(createdCredentials.email)
            }
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
          <br />
          密码：<code>{createdCredentials.password}</code>
          <IconButton
            size="small"
            onClick={() =>
              navigator.clipboard.writeText(createdCredentials.password)
            }
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Alert>
      )}

      {resetCredentials && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setResetCredentials(null)}
        >
          <AlertTitle>密码已重置 — 新密码仅显示这一次，请立即保存</AlertTitle>
          邮箱：<code>{resetCredentials.email}</code>
          <IconButton
            size="small"
            onClick={() =>
              navigator.clipboard.writeText(resetCredentials.email)
            }
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
          <br />
          新密码：<code>{resetCredentials.password}</code>
          <IconButton
            size="small"
            onClick={() =>
              navigator.clipboard.writeText(resetCredentials.password)
            }
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          创建测试账号
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshAll}
          disabled={listLoading}
        >
          刷新
        </Button>
      </Stack>

      {listLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            还没有测试账号
          </Typography>
          <Typography variant="body2" color="text.secondary">
            点击「创建测试账号」，用生成的邮箱/密码在手机 App
            上登录，然后回到这里进行会员和 session 操作。
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>邮箱</TableCell>
                <TableCell>会员</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>备注</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <React.Fragment key={account.user_id}>
                  <TableRow hover>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>
                      {account.has_membership ? (
                        <Chip
                          label={`会员至 ${formatTime(
                            account.membership_end_at
                          )}`}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip label="非会员" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {account.has_session ? (
                        <Chip label="有" size="small" color="info" />
                      ) : (
                        <Chip label="无" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{account.note || "-"}</TableCell>
                    <TableCell>{formatTime(account.created_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => toggleManage(account.email)}
                      >
                        {managedEmail === account.email ? "收起" : "管理"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                      <Collapse in={managedEmail === account.email}>
                        <Box sx={{ px: 2 }}>
                          {overviewLoading &&
                          managedEmail === account.email ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                my: 2,
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                          ) : (
                            managedEmail === account.email && managementPanel
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>创建测试账号</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="邮箱（留空自动生成）"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              helperText="填写已有账号的邮箱可将其标记为测试账号（不改密码）"
            />
            <TextField
              size="small"
              label="密码（留空自动生成）"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              helperText="至少 8 位，含大小写字母和数字"
            />
            <TextField
              size="small"
              label="备注"
              value={createNote}
              onChange={(e) => setCreateNote(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createWithMembership}
                  onChange={(e) => setCreateWithMembership(e.target.checked)}
                />
              }
              label="创建时赠送 30 天手动会员（可直接测会员内容）"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={createAccount}
            disabled={actionLoading}
          >
            创建
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)}>
        <DialogTitle>重置密码</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            将为 {resetTarget?.email} 生成一个新密码，仅显示一次，请立即保存。
            <br />
            旧密码将立即失效（已登录的设备不受影响，重新登录时需使用新密码）。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>取消</Button>
          <Button
            variant="contained"
            onClick={resetPassword}
            disabled={actionLoading}
          >
            重置密码
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default TestAccountPage;
